import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../api';
import UserAvatar from '../components/UserAvatar';
import PoolSelector from '../components/PoolSelector';
import NoPoolYet from '../components/NoPoolYet';

function logoUrl(abbr) {
  return 'https://a.espncdn.com/i/teamlogos/nfl/500/' + abbr.toLowerCase() + '.png';
}

// ── Share card (screenshot-optimised) ─────────────────────────────────────
function ShareCard({ recap, user, poolName, onClose }) {
  const cardRef = useRef(null);
  const isWinner = recap.winner?.username === user?.username;

  const handleShare = async () => {
    const text = recap.winner
      ? recap.winner.username + ' won ' + recap.week.label + ' with ' + recap.winner.correct + ' correct picks!'
        + (recap.myEntry ? ' I finished #' + recap.myEntry.rank + ' with ' + recap.correct + ' correct.' : '')
        + ' 🏈 www.rivalrypicks.com'
      : recap.week.label + ' recap. 🏈 www.rivalrypicks.com';

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Rivalry Picks: ' + recap.week.label + ' Recap',
          text: text,
          url: 'https://www.rivalrypicks.com'
        });
        return;
      } catch (e) {}
    }
    // Fallback: copy to clipboard
    navigator.clipboard.writeText(text).catch(() => {});
    alert('Copied to clipboard! Paste it anywhere to share.');
  };

  const rankColor = r => r === 1 ? '#FFD700' : r === 2 ? '#C0C0C0' : r === 3 ? '#CD7F32' : '#7fb3ff';

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', zIndex:3000, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:16 }}>
      <style>{`
        @keyframes fadeCard { from{opacity:0;transform:scale(0.95)} to{opacity:1;transform:scale(1)} }
      `}</style>

      {/* Card */}
      <div ref={cardRef} style={{ background:'linear-gradient(160deg,#010d1e 0%,#012040 50%,#010d1e 100%)', borderRadius:20, padding:28, width:'100%', maxWidth:400, border:'2px solid rgba(213,10,10,0.4)', boxShadow:'0 0 40px rgba(213,10,10,0.2)', animation:'fadeCard 0.3s ease-out' }}>

        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ display:'inline-block', background:'linear-gradient(135deg,#D50A0A,#8b0000)', padding:'4px 16px', borderRadius:20, fontSize:11, fontWeight:700, color:'#fff', letterSpacing:2, marginBottom:8 }}>
            🏈 RIVALRY PICKS
          </div>
          {poolName && (
            <div style={{ fontSize:13, color:'#7fb3ff', fontWeight:700, marginBottom:4 }}>{poolName}</div>
          )}
          <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:26, fontWeight:700, color:'#fff', letterSpacing:1 }}>
            {recap.week.label.toUpperCase()}
          </div>
          <div style={{ fontSize:13, color:'#556', letterSpacing:2 }}>WEEKLY RECAP</div>
        </div>

        {/* Winner */}
        {recap.winner && (
          <div style={{ background: isWinner ? 'linear-gradient(135deg,rgba(255,215,0,0.15),rgba(184,134,11,0.08))' : 'rgba(255,255,255,0.05)', border:'1px solid ' + (isWinner ? 'rgba(255,215,0,0.4)' : 'rgba(255,255,255,0.1)'), borderRadius:14, padding:'16px 20px', marginBottom:14, display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ position:'relative' }}>
              <UserAvatar username={recap.winner.username} size={52} style={{ boxShadow: isWinner ? '0 0 16px rgba(255,215,0,0.5)' : 'none' }} />
              <div style={{ position:'absolute', top:-10, left:'50%', transform:'translateX(-50%)', fontSize:20 }}>👑</div>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11, color:'#556', fontWeight:700, letterSpacing:1, marginBottom:4 }}>WEEK WINNER</div>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:20, fontWeight:700, color: isWinner ? '#FFD700' : '#fff' }}>
                {recap.winner.username}
              </div>
              <div style={{ fontSize:14, color:'#4caf50', fontWeight:700 }}>{recap.winner.correct} correct picks</div>
              {isWinner && <div style={{ fontSize:12, color:'#FFD700', marginTop:2 }}>That's you! 🎉</div>}
            </div>
          </div>
        )}

        {/* Your result */}
        {recap.myEntry && (
          <div style={{ background:'rgba(1,51,105,0.25)', border:'1px solid rgba(127,179,255,0.2)', borderRadius:14, padding:'14px 20px', marginBottom:14 }}>
            <div style={{ fontSize:11, color:'#556', fontWeight:700, letterSpacing:1, marginBottom:10 }}>YOUR RESULT</div>
            <div style={{ display:'flex', alignItems:'center', gap:16 }}>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:32, fontWeight:700, color: rankColor(recap.myEntry.rank) }}>
                #{recap.myEntry.rank}
              </div>
              <div style={{ flex:1, display:'flex', gap:16 }}>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:24, fontWeight:700, color:'#4caf50' }}>{recap.correct}</div>
                  <div style={{ fontSize:10, color:'#445' }}>CORRECT</div>
                </div>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:24, fontWeight:700, color:'#f44336' }}>{recap.wrong}</div>
                  <div style={{ fontSize:10, color:'#445' }}>WRONG</div>
                </div>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:16, fontWeight:700, color:'#556' }}>of {recap.lb.length}</div>
                  <div style={{ fontSize:10, color:'#445' }}>PLAYERS</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Top 5 */}
        <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'14px 20px', marginBottom:20 }}>
          <div style={{ fontSize:11, color:'#556', fontWeight:700, letterSpacing:1, marginBottom:10 }}>STANDINGS</div>
          {recap.lb.slice(0, 5).map(function(e, i) {
            const isMe = e.user_id != null ? e.user_id === user?.id : e.username === user?.username;
            return (
              <div key={e.entry_id ?? e.user_id} style={{ display:'flex', alignItems:'center', gap:10, marginBottom: i < 4 ? 8 : 0 }}>
                <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, fontWeight:700, color: rankColor(i+1), minWidth:24, textAlign:'right' }}>#{i+1}</span>
                <UserAvatar username={e.username} size={24} />
                <span style={{ fontSize:13, fontWeight:600, color: isMe ? '#7fb3ff' : '#ccd', flex:1 }}>
                  {e.username}{isMe ? ' ★' : ''}
                </span>
                <span style={{ fontSize:13, fontWeight:700, color:'#4caf50' }}>{e.correct}</span>
              </div>
            );
          })}
        </div>

        {/* Footer watermark */}
        <div style={{ textAlign:'center', fontSize:11, color:'#223' }}>
          www.rivalrypicks.com
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display:'flex', gap:12, marginTop:16 }}>
        <button
          onClick={handleShare}
          style={{ padding:'12px 28px', borderRadius:10, border:'none', cursor:'pointer', fontSize:15, fontWeight:700, fontFamily:"'Oswald',sans-serif", letterSpacing:1, background:'linear-gradient(135deg,#D50A0A,#a00808)', color:'#fff' }}
        >
          📤 SHARE
        </button>
        <button
          onClick={onClose}
          style={{ padding:'12px 20px', borderRadius:10, border:'1px solid rgba(255,255,255,0.15)', cursor:'pointer', fontSize:14, fontWeight:700, background:'transparent', color:'#aab' }}
        >
          Close
        </button>
      </div>
      <div style={{ marginTop:10, fontSize:12, color:'#334', textAlign:'center' }}>
        Screenshot this card to share in group chats
      </div>
    </div>
  );
}

// ── Main RecapPage ──────────────────────────────────────────────────────────
export default function RecapPage({ user, pools, poolsLoaded, selectedPool, onSelectPool, onManagePools }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allWeeks, setAllWeeks] = useState([]);
  const [selectedWeekId, setSelectedWeekId] = useState(null);
  const [showCard, setShowCard] = useState(false);

  const poolId = selectedPool?.id;

  const reqRef = useRef(0);
  const load = useCallback(async (weekId, pid) => {
    const ticket = ++reqRef.current;
    const stale = () => ticket !== reqRef.current;   // a newer pool/week won the race
    setLoading(true);
    try {
      const [lbRes, weeksRes] = await Promise.all([
        API.get('/picks/leaderboard' + (weekId ? '?week_id=' + weekId : '') + (pid ? (weekId ? '&' : '?') + 'pool_id=' + pid : '')),
        API.get('/picks/weeks')
      ]);

      if (stale()) return;
      const lb = lbRes.data.leaderboard || [];
      const week = lbRes.data.week;
      const weeks = weeksRes.data.weeks || [];

      // Filter to finalized weeks
      const finalizedWeeks = weeks.filter(w =>
        lb.length > 0 || w.id !== week?.id
      );
      setAllWeeks(weeks);

      if (!week || lb.length === 0) { setLoading(false); setData(null); return; }

      const winner = lb[0];
      const myEntry = lb.find(e => e.username === user?.username);
      // Take my own numbers from the (week-scoped) leaderboard row, not from
      // /picks/mine — that endpoint always returns the CURRENT week, so
      // selecting an earlier week left "Your Week" showing this week's record
      // beside last week's standings.
      const correct = myEntry?.correct ?? 0;
      const wrong = myEntry?.wrong ?? 0;
      const pending = myEntry?.pending ?? 0;
      const total = correct + wrong + pending;

      // Whether the WEEK is done is a property of the games, not of your own
      // picks. Deriving it from picks meant anyone who didn't play saw a
      // finished week labelled "IN PROGRESS" — exactly the person most likely to
      // open the recap to see what they missed.
      const totalGames = lbRes.data.totalGames ?? 0;
      const completedGames = lbRes.data.completedGames ?? 0;
      const weekFinalized = totalGames > 0 && completedGames >= totalGames;

      setData({ week, winner, myEntry, correct, wrong, pending, total, lb, weekFinalized });
    } catch (e) {
      if (!stale()) setData(null);
    }
    if (!stale()) setLoading(false);
  }, [user]);

  useEffect(() => {
    load(selectedWeekId, poolId);
  }, [load, selectedWeekId, poolId]);

  // The third board with the same hole as the weekly and season ones, and the
  // worst of the three to get wrong: with no pool the request goes out unscoped,
  // so the recap crowned a "WEEK WINNER" the reader has never met and listed
  // standings they were never part of. A recap is a summary of YOUR week in YOUR
  // pool — with no pool there is no week to summarise.
  if (poolsLoaded && (!pools || pools.length === 0)) return <NoPoolYet onGoToPools={onManagePools} board="recap" />;

  const poolName = selectedPool?.name || (pools && pools[0]?.name) || null;

  return (
    <div style={{ maxWidth:860, margin:'0 auto', padding:'24px 16px' }}>
      <PoolSelector pools={pools} selectedPool={selectedPool} onSelect={onSelectPool} onManage={onManagePools} />

      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:28, fontWeight:700, color:'#fff', marginBottom:4 }}>
          📋 WEEKLY RECAP
        </div>
        <div style={{ color:'#556', fontSize:14 }}>Results and standings after each week finalizes.</div>
      </div>

      {/* Week selector */}
      {allWeeks.length > 1 && (
        <div style={{ marginBottom:20 }}>
          <select
            value={selectedWeekId || ''}
            onChange={e => setSelectedWeekId(e.target.value || null)}
            style={{ padding:'8px 14px', background:'#0d1525', border:'1px solid rgba(255,255,255,0.15)', borderRadius:8, color:'#fff', fontSize:14, outline:'none', cursor:'pointer' }}
          >
            <option value=''>Current Week</option>
            {allWeeks.map(w => (
              <option key={w.id} value={w.id}>{w.label}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign:'center', paddingTop:60 }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
          <div style={{ color:'#556', fontFamily:"'Oswald',sans-serif", fontSize:16, letterSpacing:1 }}>LOADING RECAP...</div>
        </div>
      ) : !data ? (
        <div style={{ textAlign:'center', paddingTop:60 }}>
          <div style={{ fontSize:56, marginBottom:16 }}>⏳</div>
          <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:22, color:'#556', marginBottom:8 }}>NO RECAP YET</div>
          <div style={{ color:'#445', fontSize:14, maxWidth:320, margin:'0 auto' }}>
            Recap appears after the week's games are completed.
          </div>
        </div>
      ) : (
        <div>
          {/* Week badge */}
          <div style={{ display:'inline-block', background:'linear-gradient(135deg,#013369,#0a5299)', padding:'6px 18px', borderRadius:20, fontSize:12, fontWeight:700, color:'#7fb3ff', letterSpacing:1, marginBottom:20 }}>
            🏈 {data.week.label}
            {!data.weekFinalized && <span style={{ color:'#ffa000', marginLeft:8 }}>· IN PROGRESS</span>}
          </div>

          {/* Winner */}
          {data.winner && (
            <div style={{ background: data.winner.username === user?.username ? 'linear-gradient(135deg,rgba(255,215,0,0.12),rgba(184,134,11,0.06))' : '#0d1525', border:'1px solid ' + (data.winner.username === user?.username ? 'rgba(255,215,0,0.35)' : 'rgba(255,255,255,0.08)'), borderRadius:14, padding:'20px 24px', marginBottom:16, display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
              <div style={{ position:'relative' }}>
                <UserAvatar username={data.winner.username} size={60} style={{ boxShadow: data.winner.username === user?.username ? '0 0 20px rgba(255,215,0,0.4)' : 'none' }} />
                <div style={{ position:'absolute', top:-12, left:'50%', transform:'translateX(-50%)', fontSize:22 }}>👑</div>
              </div>
              <div style={{ flex:1 }}>
                {/* Nothing is won until the last game is final — mid-week this
                    is only whoever happens to be ahead right now. */}
                <div style={{ fontSize:11, fontWeight:700, color:'#556', letterSpacing:1, marginBottom:4 }}>
                  {data.weekFinalized ? 'WEEK WINNER' : 'CURRENT LEADER'}
                </div>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:24, fontWeight:700, color: data.winner.username === user?.username ? '#FFD700' : '#fff' }}>
                  {data.winner.username}
                  {data.winner.username === user?.username && (
                    <span style={{ fontSize:14, color:'#FFD700', marginLeft:8 }}>
                      {data.weekFinalized ? "that's you! 🎉" : "that's you"}
                    </span>
                  )}
                </div>
                <div style={{ fontSize:14, color:'#4caf50', fontWeight:700 }}>
                  {data.winner.correct} correct{data.weekFinalized ? ' picks this week' : ' so far'}
                </div>
              </div>
              {/* Only offer a shareable card once the week is actually settled. */}
              {data.weekFinalized && (
                <button
                  onClick={() => setShowCard(true)}
                  style={{ padding:'10px 22px', borderRadius:10, border:'none', cursor:'pointer', fontSize:14, fontWeight:700, fontFamily:"'Oswald',sans-serif", letterSpacing:1, background:'linear-gradient(135deg,#D50A0A,#a00808)', color:'#fff', flexShrink:0 }}
                >
                  📤 Share Recap
                </button>
              )}
            </div>
          )}

          {/* Your stats */}
          {data.myEntry && (
            <div style={{ background:'rgba(1,51,105,0.2)', border:'1px solid rgba(127,179,255,0.15)', borderRadius:14, padding:'18px 24px', marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#556', letterSpacing:1, marginBottom:14 }}>YOUR WEEK</div>
              <div style={{ display:'flex', gap:20, flexWrap:'wrap', alignItems:'center' }}>
                <div style={{ textAlign:'center', minWidth:60 }}>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:36, fontWeight:700, color: data.myEntry.rank === 1 ? '#FFD700' : data.myEntry.rank === 2 ? '#C0C0C0' : data.myEntry.rank === 3 ? '#CD7F32' : '#7fb3ff' }}>
                    #{data.myEntry.rank}
                  </div>
                  <div style={{ fontSize:11, color:'#445' }}>RANK</div>
                </div>
                <div style={{ width:1, height:50, background:'rgba(255,255,255,0.08)' }} />
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:32, fontWeight:700, color:'#4caf50' }}>{data.correct}</div>
                  <div style={{ fontSize:11, color:'#445' }}>CORRECT</div>
                </div>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:32, fontWeight:700, color:'#f44336' }}>{data.wrong}</div>
                  <div style={{ fontSize:11, color:'#445' }}>WRONG</div>
                </div>
                {data.pending > 0 && (
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:24, fontWeight:700, color:'#778' }}>{data.pending}</div>
                    <div style={{ fontSize:11, color:'#445' }}>PENDING</div>
                  </div>
                )}
                <div style={{ textAlign:'center', marginLeft:'auto' }}>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:18, fontWeight:700, color:'#556' }}>of {data.lb.length}</div>
                  <div style={{ fontSize:11, color:'#445' }}>PLAYERS</div>
                </div>
              </div>
            </div>
          )}

          {/* Pool standings */}
          <div style={{ background:'#0d1525', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'18px 24px' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#556', letterSpacing:1, marginBottom:14 }}>POOL STANDINGS</div>
            {data.lb.slice(0, 10).map(function(e, i) {
              const isMe = e.user_id != null ? e.user_id === user?.id : e.username === user?.username;
              const rankCol = e.rank === 1 ? '#FFD700' : e.rank === 2 ? '#C0C0C0' : e.rank === 3 ? '#CD7F32' : '#445';
              return (
                <div key={e.entry_id ?? e.user_id} style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 0', borderBottom: i < data.lb.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none', background: isMe ? 'rgba(1,51,105,0.15)' : 'transparent', borderRadius:6, paddingLeft: isMe ? 8 : 0 }}>
                  <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:16, fontWeight:700, color:rankCol, minWidth:32, textAlign:'right' }}>#{e.rank}</span>
                  <UserAvatar username={e.username} size={32} />
                  <span style={{ fontSize:14, fontWeight:600, color: isMe ? '#7fb3ff' : '#ccd', flex:1 }}>
                    {e.username}{isMe ? ' (you)' : ''}
                  </span>
                  <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:18, fontWeight:700, color:'#4caf50' }}>{e.correct}</span>
                  <span style={{ fontSize:12, color:'#445' }}>correct</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Share card modal */}
      {showCard && data && (
        <ShareCard
          recap={data}
          user={user}
          poolName={poolName}
          onClose={() => setShowCard(false)}
        />
      )}
    </div>
  );
}
